import { describeCurrentUserLicensesFailure } from '@ns/plugin-ns-tessa-common/current-user-licenses-loader';
import {
  describeSourceMismatch,
  toSubmitValidator,
} from '@ns/plugin-picker-shared';

import {
  buildSignedInUserQueryOptions,
  buildUserLicensesQueryOptions,
  readUserLicensesTransport,
  TESSA_USER_LICENSES_FORBIDDEN_MESSAGE,
  tessaQueryClient,
  type TessaUserLicense,
  TessaUserLicensesUnavailableError,
  toLicenseOptions,
} from './tessa-user-licenses-query';

export const TESSA_USER_LICENSE_PICKER = 'TessaUserLicensePicker';

/** Whether a restored value is exactly what the picker writes: `id` and `name`, both non-blank strings, and no other key. */
function isSelection(data: unknown): data is TessaUserLicense {
  if (typeof data !== 'object' || data === null || Array.isArray(data)) {
    return false;
  }
  const value = new Map<string, unknown>(Object.entries(data));
  const id = value.get('id');
  const name = value.get('name');
  return (
    value.size === 2 &&
    typeof id === 'string' &&
    id.trim() !== '' &&
    typeof name === 'string' &&
    name.trim() !== ''
  );
}

/**
 * Validates that the chosen license is still one of the signed-in user's.
 *
 * @remarks
 * It reads the same cached user and lookup as the picker, under the same key,
 * so it asks Tessa again only when that answer is older than a minute. The
 * answer is the user's whole list, so a missing key means the license is no
 * longer held. Only the key is compared: the name is display text.
 *
 * Lookup switched off and no access block with the picker's own messages.
 * Every other failure follows picker-shared's policy: a temporary one lets
 * the submit through, which is safe because `tessa:license-revoke` checks
 * ownership again on the backend; a permanent one reports picker-shared's
 * sentence; anything else blocks with its fixed sentence and is logged.
 */
export const userLicenseValidator = toSubmitValidator<
  TessaUserLicense,
  undefined
>(
  async (value, context) => {
    if (!isSelection(value)) {
      return 'The selected license does not match what the picker writes. Choose it again.';
    }
    const transport = readUserLicensesTransport(context.apiHolder);
    const userEntityRef = await tessaQueryClient.query(
      buildSignedInUserQueryOptions(transport.identityApi),
    );
    let licenses;
    try {
      licenses = await tessaQueryClient.query(
        buildUserLicensesQueryOptions(userEntityRef, transport),
      );
    } catch (error) {
      if (error instanceof TessaUserLicensesUnavailableError) {
        return error.message;
      }
      if (describeCurrentUserLicensesFailure(error)?.status === 403) {
        return TESSA_USER_LICENSES_FORBIDDEN_MESSAGE;
      }
      throw error;
    }
    return describeSourceMismatch<TessaUserLicense, TessaUserLicense>({
      isCaseInsensitive: false,
      isComplete: true,
      items: toLicenseOptions(licenses),
      sourceFields: [],
      subject: 'license',
      submittedValue: value,
    });
  },
  {
    describeFailure: describeCurrentUserLicensesFailure,
    fieldName: TESSA_USER_LICENSE_PICKER,
    logPrefix: 'ns-tessa-scaffolder-field userLicenseValidator',
    subject: 'license',
  },
);
